import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";
import { LIST_ACTIONS, LIST_HEADER_TYPE_LOOKUP } from "../../../constants";

interface RowDetailDialogProps {
  open: boolean;
  onClose: () => void;
  listId: string;
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  row: any | null;
  headers: any[];
  onRowSaved: (updatedRow: any) => void;
}

const formatDate = (value: any): string => {
  if (!value) return "";
  const date = new Date(String(value));
  if (isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const formatChangedBy = (value: any): string => {
  if (!value) return "Unknown";
  const raw = String(value);
  if (!raw.startsWith("action:")) return raw;
  const actionType = raw.slice("action:".length);
  if (actionType === "import") return "Bulk import";
  const match = LIST_ACTIONS.find((a) => a.type === actionType);
  return match?.title || actionType;
};

const RowDetailDialog: React.FC<RowDetailDialogProps> = ({
  open,
  onClose,
  listId,
  companyId,
  getToken,
  eventListener,
  row,
  headers,
  onRowSaved,
}) => {
  const [tab, setTab] = useState(0);
  const [values, setValues] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const editableHeaders = useMemo(
    () =>
      headers.filter(
        (h) => h.type !== "action" && h.type !== LIST_HEADER_TYPE_LOOKUP
      ),
    [headers]
  );

  useEffect(() => {
    if (!open || !row) return;
    const initial: Record<string, any> = {};
    editableHeaders.forEach((h) => {
      initial[h.name] = row[h.name] ?? "";
    });
    setValues(initial);
    setTab(0);
    setHistory([]);
  }, [open, row, editableHeaders]);

  useEffect(() => {
    if (!open || !row || tab !== 1) return;
    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/lists/${listId}/row/${row.rowId}/history`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setHistory(response.data.history || []);
      } catch (err) {
        eventListener({ type: "SET_ERROR", error: parseError(err) });
      } finally {
        setHistoryLoading(false);
      }
    };
    fetchHistory();
  }, [open, row, tab, listId, companyId, getToken, eventListener]);

  const handleSave = async () => {
    if (!row) return;
    setSaving(true);
    try {
      const token = await getToken();
      const payload = { rowId: row.rowId, ...values };
      await axios.put(
        `${hostname}/lists/${listId}/row`,
        { companyId, row: payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onRowSaved({ ...row, ...values });
      onClose();
    } catch (err) {
      eventListener({ type: "SET_ERROR", error: parseError(err) });
    } finally {
      setSaving(false);
    }
  };

  const renderField = (header: any) => {
    const value = values[header.name] ?? "";
    const onChange = (newValue: string) =>
      setValues((prev) => ({ ...prev, [header.name]: newValue }));

    if (header.dataType === "date") {
      const dateValue = (() => {
        if (!value) return "";
        const d = new Date(String(value));
        if (isNaN(d.getTime())) return "";
        return d.toISOString().slice(0, 10);
      })();
      return (
        <TextField
          key={header.id}
          label={header.name}
          type="date"
          value={dateValue}
          onChange={(e) => onChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      );
    }

    if (header.dataType === "long_text") {
      return (
        <TextField
          key={header.id}
          label={header.name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          multiline
          minRows={3}
          fullWidth
        />
      );
    }

    return (
      <TextField
        key={header.id}
        label={header.name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        fullWidth
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { height: "80vh" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Row Details
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Details" />
          <Tab label="History" />
        </Tabs>
      </Box>
      <DialogContent>
        {tab === 0 && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {editableHeaders.map(renderField)}
            {row?.dateAdded && (
              <TextField
                label="Date Added"
                value={formatDate(row.dateAdded)}
                disabled
                fullWidth
              />
            )}
          </Stack>
        )}
        {tab === 1 && (
          <Box sx={{ pt: 1 }}>
            {historyLoading && (
              <Typography color="text.secondary">Loading history...</Typography>
            )}
            {!historyLoading && history.length === 0 && (
              <Typography color="text.secondary">
                No changes recorded for this row yet.
              </Typography>
            )}
            {!historyLoading &&
              history.map((entry) => (
                <Box
                  key={entry.id}
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    py: 1.5,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(entry.created_date)} ·{" "}
                    {formatChangedBy(entry.changedByUid)}
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {Object.entries(entry.changes || {}).map(
                      ([field, change]: [string, any]) => (
                        <Box key={field}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {field}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ wordBreak: "break-word" }}
                          >
                            {change.old ?? <em>empty</em>} →{" "}
                            {change.new ?? <em>empty</em>}
                          </Typography>
                        </Box>
                      )
                    )}
                  </Stack>
                </Box>
              ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {tab === 0 && (
          <LoadingButton
            loading={saving}
            onClick={handleSave}
            variant="contained"
          >
            Save
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RowDetailDialog;
